import React, { useState, useEffect } from 'react';
import { X, Plus, Save, Clock, Calendar, Pill } from 'lucide-react';

const C = {
  primary: "#7f00ff",
  primarySoft: "#f1e6ff",
  bg: "#ffffff",
  fg: "#0f172a",
  mutedFg: "#6b7a82",
  border: "#eceef1",
  good: "#16a34a",
  goodSoft: "#dcfce7",
  warn: "#f59e0b",
  warnSoft: "#fef3c7",
  danger: "#ef4444",
  dangerSoft: "#fee2e2",
};

interface MedicationModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (medication: any) => void;
  medication?: any;
  initialMedication?: any;
  mode: 'add' | 'edit';
}

export default function MedicationModal({ open, onClose, onSave, medication, mode, initialMedication }: MedicationModalProps) {
  const [formData, setFormData] = useState({
    medicineName: '',
    brandName: '',
    genericName: '',
    strength: '',
    doctorName: '',
    prescriptionDate: '',
    dosages: [''],
    frequency: '',
    duration: '',
    durationEndDate: '',
    instructions: '',
    sideEffects: '',
    category: '',
  });

  useEffect(() => {
    if (medication && mode === 'edit') {
      // Extract times from dosages array (backend uses dosages)
      const dosages = medication.dosages || [];
      const times = dosages.map((d: any) => d.time) || [''];
      
      setFormData({
        medicineName: medication.medicineName || '',
        brandName: medication.brandName || '',
        genericName: medication.genericName || '',
        strength: medication.strength || '',
        doctorName: medication.doctorName || '',
        prescriptionDate: medication.prescriptionDate ? medication.prescriptionDate.split('T')[0] : '',
        dosages: times.length ? times : [''],
        frequency: medication.frequency || '',
        duration: medication.duration || '',
        durationEndDate: medication.durationEndDate ? medication.durationEndDate.split('T')[0] : '',
        instructions: medication.instructions || '',
        // If sideEffects is an array, join with commas
        sideEffects: Array.isArray(medication.sideEffects) 
          ? medication.sideEffects.join(', ') 
          : medication.sideEffects || '',
        category: medication.category || '',
      });
    } else if (initialMedication && mode === 'add') {
      setFormData({
        medicineName: initialMedication.medicineName || '',
        brandName: initialMedication.brandName || '',
        genericName: initialMedication.genericName || '',
        strength: initialMedication.strength || '',
        doctorName: '',
        prescriptionDate: '',
        dosages: [''],
        frequency: '',
        duration: '',
        durationEndDate: '',
        instructions: '',
        sideEffects: '',
        category: initialMedication.category || '',
      });
    } else {
      setFormData({
        medicineName: '',
        brandName: '',
        genericName: '',
        strength: '',
        doctorName: '',
        prescriptionDate: '',
        dosages: [''],
        frequency: '',
        duration: '',
        durationEndDate: '',
        instructions: '',
        sideEffects: '',
        category: '',
      });
    }
  }, [medication, mode, open]);

  if (!open) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Filter out empty times
    const filteredTimes = formData.dosages.filter(t => t.trim() !== '');
    
    // Build payload matching backend schema
    const payload = {
      medicineName: formData.medicineName,
      brandName: formData.brandName,
      genericName: formData.genericName,
      strength: formData.strength,
      doctorName: formData.doctorName,
      prescriptionDate: formData.prescriptionDate || new Date().toISOString().split('T')[0],
      dosages: filteredTimes.map(time => ({
        time,
        taken: false,
      })),
      frequency: formData.frequency,
      duration: formData.duration,
      durationEndDate: formData.durationEndDate || undefined,
      instructions: formData.instructions,
      sideEffects: formData.sideEffects || '',
      category: formData.category,
    };

    onSave(payload);
  };

  const addTime = () => {
    setFormData({ ...formData, dosages: [...formData.dosages, ''] });
  };

  const removeTime = (index: number) => {
    if (formData.dosages.length > 1) {
      const newTimes = formData.dosages.filter((_, i) => i !== index);
      setFormData({ ...formData, dosages: newTimes });
    }
  };

  const updateTime = (index: number, value: string) => {
    const newTimes = [...formData.dosages];
    newTimes[index] = value;
    setFormData({ ...formData, dosages: newTimes });
  };

  const frequencyOptions = [
    'Once daily',
    'Twice daily',
    'Three times daily',
    'Four times daily',
    'Every 4 hours',
    'Every 6 hours',
    'Every 8 hours',
    'Every 12 hours',
    'As needed',
    'Weekly',
    'Monthly'
  ];

  const categoryOptions = [
    'Antibiotic',
    'Antidepressant',
    'Antihistamine',
    'Blood Pressure',
    'Cholesterol',
    'Diabetes',
    'Pain Relief',
    'Vitamin/Supplement',
    'Other'
  ];

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        backdropFilter: 'blur(8px)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        overflowY: 'auto',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: C.bg,
          borderRadius: 24,
          maxWidth: 600,
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          padding: 32,
          position: 'relative',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: C.fg }}>
            <Pill size={24} style={{ display: 'inline', marginRight: 10 }} />
            {mode === 'add' ? 'Add New Medication' : 'Edit Medication'}
          </h3>
          <button
            onClick={onClose}
            style={{
              width: 36,
              height: 36,
              borderRadius: 999,
              border: 'none',
              background: C.border,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <X size={18} color={C.fg} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Medicine Name */}
            <div>
              <label style={{ fontSize: 14, fontWeight: 600, color: C.fg, display: 'block', marginBottom: 4 }}>
                Medicine Name *
              </label>
              <input
                type="text"
                value={formData.medicineName}
                onChange={(e) => setFormData({ ...formData, medicineName: e.target.value })}
                placeholder="e.g., Lisinopril"
                required
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1px solid ${C.border}`,
                  borderRadius: 10,
                  fontSize: 14,
                  outline: 'none',
                }}
              />
            </div>

            {/* Brand Name */}
            <div>
              <label style={{ fontSize: 14, fontWeight: 600, color: C.fg, display: 'block', marginBottom: 4 }}>
                Brand Name
              </label>
              <input
                type="text"
                value={formData.brandName}
                onChange={(e) => setFormData({ ...formData, brandName: e.target.value })}
                placeholder="e.g., Zestril"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1px solid ${C.border}`,
                  borderRadius: 10,
                  fontSize: 14,
                  outline: 'none',
                }}
              />
            </div>

            {/* Generic Name */}
            <div>
              <label style={{ fontSize: 14, fontWeight: 600, color: C.fg, display: 'block', marginBottom: 4 }}>
                Generic Name
              </label>
              <input
                type="text"
                value={formData.genericName}
                onChange={(e) => setFormData({ ...formData, genericName: e.target.value })}
                placeholder="e.g., Lisinopril"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1px solid ${C.border}`,
                  borderRadius: 10,
                  fontSize: 14,
                  outline: 'none',
                }}
              />
            </div>

            {/* Strength */}
            <div>
              <label style={{ fontSize: 14, fontWeight: 600, color: C.fg, display: 'block', marginBottom: 4 }}>
                Strength *
              </label>
              <input
                type="text"
                value={formData.strength}
                onChange={(e) => setFormData({ ...formData, strength: e.target.value })}
                placeholder="e.g., 10mg"
                required
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1px solid ${C.border}`,
                  borderRadius: 10,
                  fontSize: 14,
                  outline: 'none',
                }}
              />
            </div>

            {/* Category */}
            <div>
              <label style={{ fontSize: 14, fontWeight: 600, color: C.fg, display: 'block', marginBottom: 4 }}>
                Category
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1px solid ${C.border}`,
                  borderRadius: 10,
                  fontSize: 14,
                  outline: 'none',
                  background: C.bg,
                }}
              >
                <option value="">Select category</option>
                {categoryOptions.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>

            {/* Doctor Name */}
            <div>
              <label style={{ fontSize: 14, fontWeight: 600, color: C.fg, display: 'block', marginBottom: 4 }}>
                Prescribed By
              </label>
              <input
                type="text"
                value={formData.doctorName}
                onChange={(e) => setFormData({ ...formData, doctorName: e.target.value })}
                placeholder="e.g., Dr. Smith"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1px solid ${C.border}`,
                  borderRadius: 10,
                  fontSize: 14,
                  outline: 'none',
                }}
              />
            </div>

            {/* Prescription Date */}
            <div>
              <label style={{ fontSize: 14, fontWeight: 600, color: C.fg, display: 'block', marginBottom: 4 }}>
                Prescription Date
              </label>
              <input
                type="date"
                value={formData.prescriptionDate}
                onChange={(e) => setFormData({ ...formData, prescriptionDate: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1px solid ${C.border}`,
                  borderRadius: 10,
                  fontSize: 14,
                  outline: 'none',
                }}
              />
            </div>

            {/* Frequency */}
            <div>
              <label style={{ fontSize: 14, fontWeight: 600, color: C.fg, display: 'block', marginBottom: 4 }}>
                Frequency *
              </label>
              <select
                value={formData.frequency}
                onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1px solid ${C.border}`,
                  borderRadius: 10,
                  fontSize: 14,
                  outline: 'none',
                  background: C.bg,
                }}
              >
                <option value="">Select frequency</option>
                {frequencyOptions.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>

            {/* Dosages */}
            <div>
              <label style={{ fontSize: 14, fontWeight: 600, color: C.fg, display: 'block', marginBottom: 4 }}>
                <Clock size={14} style={{ display: 'inline', marginRight: 6 }} />
                Dosage Times *
              </label>
              {formData.dosages.map((time, index) => (
                <div key={index} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <input
                    type="time"
                    value={time}
                    onChange={(e) => updateTime(index, e.target.value)}
                    required
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      border: `1px solid ${C.border}`,
                      borderRadius: 10,
                      fontSize: 14,
                      outline: 'none',
                    }}
                  />
                  {formData.dosages.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeTime(index)}
                      style={{
                        padding: '8px 12px',
                        borderRadius: 10,
                        border: `1px solid ${C.dangerSoft}`,
                        background: C.dangerSoft,
                        color: C.danger,
                        cursor: 'pointer',
                      }}
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addTime}
                style={{
                  padding: '6px 12px',
                  borderRadius: 8,
                  border: `1px dashed ${C.primary}`,
                  background: 'transparent',
                  color: C.primary,
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                <Plus size={14} style={{ display: 'inline', marginRight: 4 }} />
                Add Time
              </button>
            </div>

            {/* Duration */}
            <div>
              <label style={{ fontSize: 14, fontWeight: 600, color: C.fg, display: 'block', marginBottom: 4 }}>
                Duration
              </label>
              <input
                type="text"
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                placeholder="e.g., 30 days"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1px solid ${C.border}`,
                  borderRadius: 10,
                  fontSize: 14,
                  outline: 'none',
                }}
              />
            </div>

            {/* End Date */}
            <div>
              <label style={{ fontSize: 14, fontWeight: 600, color: C.fg, display: 'block', marginBottom: 4 }}>
                <Calendar size={14} style={{ display: 'inline', marginRight: 6 }} />
                End Date
              </label>
              <input
                type="date"
                value={formData.durationEndDate}
                onChange={(e) => setFormData({ ...formData, durationEndDate: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1px solid ${C.border}`,
                  borderRadius: 10,
                  fontSize: 14,
                  outline: 'none',
                }}
              />
            </div>

            {/* Instructions */}
            <div>
              <label style={{ fontSize: 14, fontWeight: 600, color: C.fg, display: 'block', marginBottom: 4 }}>
                Instructions
              </label>
              <textarea
                value={formData.instructions}
                onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                placeholder="e.g., Take with food, avoid alcohol"
                rows={2}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1px solid ${C.border}`,
                  borderRadius: 10,
                  fontSize: 14,
                  outline: 'none',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                }}
              />
            </div>

            {/* Side Effects */}
            <div>
              <label style={{ fontSize: 14, fontWeight: 600, color: C.fg, display: 'block', marginBottom: 4 }}>
                Side Effects
              </label>
              <input
                type="text"
                value={formData.sideEffects}
                onChange={(e) => setFormData({ ...formData, sideEffects: e.target.value })}
                placeholder="e.g., Dizziness, Headache (comma separated)"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1px solid ${C.border}`,
                  borderRadius: 10,
                  fontSize: 14,
                  outline: 'none',
                }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                flex: 1,
                padding: '12px',
                borderRadius: 12,
                border: `1px solid ${C.border}`,
                background: C.bg,
                color: C.fg,
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                flex: 2,
                padding: '12px',
                borderRadius: 12,
                border: 'none',
                background: C.primary,
                color: '#fff',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              <Save size={16} />
              {mode === 'add' ? 'Add Medication' : 'Update Medication'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}